import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

// styles
import * as styles from "./CarouselV2.module.css";

// icons
import leftArrow from "../../images/icons/left_arrw.png";
import rightArrow from "../../images/icons/right_arrw.png";

const CarouselV2 = ({
  items = [],
  slidesToShow = 2,
  slidesToShowMobile = 1,
  gap = 24,
  autoPlay = false,
  autoPlayInterval = 5000,
  showArrows = true,
  showCounter = true,
  heading = "TWIN HEALTH INDIA MEMBER",
  subHeading = "Success Stories",
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slidesToShowCurrent, setSlidesToShowCurrent] = useState(slidesToShow);
  const [isMobile, setIsMobile] = useState(false);
  const autoPlayRef = useRef(null);

  // Calculate total slides based on screen size
  const totalSlides = items.length;
  const maxIndex = Math.max(0, totalSlides - slidesToShowCurrent);

  // Handle responsive slides
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 767) {
        setSlidesToShowCurrent(slidesToShowMobile);
        setIsMobile(true);
      } else {
        setSlidesToShowCurrent(slidesToShow);
        setIsMobile(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [slidesToShow, slidesToShowMobile]);

  // Auto play functionality
  useEffect(() => {
    if (autoPlay) {
      autoPlayRef.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentIndex(prev => {
          if (prev >= maxIndex) {
            return 0;
          }
          return prev + 1;
        });
        setTimeout(() => setIsTransitioning(false), 600);
      }, autoPlayInterval);

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
  }, [autoPlay, autoPlayInterval, maxIndex]);

  const handlePrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.max(0, prev - 1));
    setTimeout(() => setIsTransitioning(false), 600);

    // Reset autoplay timer
    if (autoPlay && autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentIndex(prev => {
          if (prev >= maxIndex) {
            return 0;
          }
          return prev + 1;
        });
        setTimeout(() => setIsTransitioning(false), 600);
      }, autoPlayInterval);
    }
  };

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
    setTimeout(() => setIsTransitioning(false), 600);

    // Reset autoplay timer
    if (autoPlay && autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentIndex(prev => {
          if (prev >= maxIndex) {
            return 0;
          }
          return prev + 1;
        });
        setTimeout(() => setIsTransitioning(false), 600);
      }, autoPlayInterval);
    }
  };

  // Calculate transform
  const slideWidth = 100 / slidesToShowCurrent;
  const gapOffset = isMobile ? 0 : currentIndex * gap;
  const transform = isMobile
    ? "none"
    : `translateX(calc(-${currentIndex * slideWidth}% - ${gapOffset}px))`;

  // For mobile, only show current item
  const displayItems = isMobile ? [items[currentIndex]] : items;

  return (
    <section className={styles.carouselV2Section}>
      <div className="container">
        <div className={styles.carouselV2Header}>
          <div className={styles.carouselV2Heading}>
            <p className={styles.carouselV2Subtitle}>{heading}</p>
            <h2 className={styles.carouselV2Title}>{subHeading}</h2>
          </div>
        </div>

        <div className={styles.carouselV2Wrapper}>
          <div className={styles.carouselV2Container}>
            <div
              className={styles.carouselV2Track}
              style={{
                transform,
                transition:
                  isTransitioning && !isMobile
                    ? "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                    : "none",
              }}
            >
              {displayItems.map((item, index) => {
                const actualIndex = isMobile ? currentIndex : index;
                return (
                  <div
                    key={actualIndex}
                    className={styles.carouselV2Slide}
                    style={{
                      flex: isMobile
                        ? "0 0 100%"
                        : `0 0 calc(${slideWidth}% - ${gap}px)`,
                      marginRight:
                        !isMobile && index < items.length - 1
                          ? `${gap}px`
                          : "0",
                    }}
                  >
                    <div className={styles.testimonialCard}>
                      <p className={styles.testimonialQuote}>{item.quote}</p>
                      <div className={styles.testimonialAuthor}>
                        <div className={styles.authorIcon}>
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="12" cy="12" r="12" fill="#AD2576" />
                            <path
                              d="M12 12C13.6569 12 15 10.6569 15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12Z"
                              fill="white"
                            />
                            <path
                              d="M12 13.5C9.51472 13.5 7.5 15.5147 7.5 18V18.75H16.5V18C16.5 15.5147 14.4853 13.5 12 13.5Z"
                              fill="white"
                            />
                          </svg>
                        </div>
                        <div className={styles.authorInfo}>
                          <p className={styles.authorName}>{item.name}</p>
                          <p className={styles.authorRole}>{item.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation Controls */}
          {(showCounter || showArrows) && (
            <div className={styles.carouselV2Controls}>
              {/* Counter */}
              {showCounter && (
                <div className={styles.carouselV2Counter}>
                  <span className={styles.counterCurrent}>
                    {String(currentIndex + 1).padStart(2, "0")}
                  </span>
                  <span className={styles.counterSeparator}> / </span>
                  <span className={styles.counterTotal}>
                    {String(totalSlides).padStart(2, "0")}
                  </span>
                </div>
              )}

              {/* Arrow Buttons */}
              {showArrows && totalSlides > slidesToShowCurrent && (
                <div className={styles.carouselV2Arrows}>
                  <button
                    className={`${styles.arrowButtonV2} ${
                      currentIndex === 0 ? styles.arrowDisabledV2 : ""
                    }`}
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                    aria-label="Previous slide"
                  >
                    <img src={leftArrow} alt="Previous" />
                  </button>
                  <button
                    className={`${styles.arrowButtonV2} ${
                      currentIndex === maxIndex ? styles.arrowDisabledV2 : ""
                    }`}
                    onClick={handleNext}
                    disabled={currentIndex === maxIndex}
                    aria-label="Next slide"
                  >
                    <img src={rightArrow} alt="Next" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

CarouselV2.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      quote: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
    })
  ).isRequired,
  slidesToShow: PropTypes.number,
  slidesToShowMobile: PropTypes.number,
  gap: PropTypes.number,
  autoPlay: PropTypes.bool,
  autoPlayInterval: PropTypes.number,
  showArrows: PropTypes.bool,
  showCounter: PropTypes.bool,
};

export default CarouselV2;
